import { GridLogicOperator, GridFilterModel as MuiGridFilterModel, GridSortModel as MuiGridSortModel } from '@mui/x-data-grid'
import { AssertNeverError, AvailableLocale } from '@omnea-digital/toolbox'
import { GridFilterItem, GridFilterModel, GridFilterItemSchema } from '@omnea-app/packages/universal/tables/gridModel'
import { ROW_GROUPING_SINGLE_GROUPING_FIELD } from '../row-grouping/helpers'

interface TransformFilterModelOptions {
  filterModel?: MuiGridFilterModel
  filterNullValues?: boolean
  locale?: AvailableLocale
  // The active grouping fields, when the grid is grouped. Used to resolve MUI's grouping pseudo-field.
  groupFields?: readonly string[]
}
interface TransformSortModelOptions {
  sortModel?: MuiGridSortModel
  // The active grouping fields, when the grid is grouped. Used to resolve MUI's grouping pseudo-field.
  groupFields?: readonly string[]
}

// The single grouping field MUI's pseudo-field maps onto, or undefined when grouping is off or multi-level
// (neither is server-served).
const resolveSingleGroupField = (groupFields: readonly string[] | undefined): string | undefined =>
  groupFields?.length === 1 ? groupFields[0] : undefined

// The group-column header sends MUI's grouping pseudo-field. A grouping-aware caller (groupFields present)
// maps it to the real column, or strips it (undefined groupField) when there's no single field to map onto.
// A caller with no groupFields (URL-state sync) isn't server-bound, so the pseudo-field is kept — dropping
// it would lose the clause.
const resolveFilterField = (
  itemField: string,
  groupFields: readonly string[] | undefined,
  groupField: string | undefined
): string | undefined => {
  if (itemField !== ROW_GROUPING_SINGLE_GROUPING_FIELD) return itemField
  return groupFields === undefined ? itemField : groupField
}

// Rewrites MUI's grouping pseudo-field to the real grouping column on each filter item. Field-keyed
// transforms a caller runs before the request body — request status coercion — only fire on the true
// field, so a group-column filter must be resolved before them, not only inside the body transform. Only
// the single-served-group case is rewritten; ungrouped or multi-level models are left untouched, matching
// transformMuiFilterModelToOmnea's own resolution.
export const resolveGroupingFilterFields = (
  filterModel: MuiGridFilterModel,
  groupFields: readonly string[] | undefined
): MuiGridFilterModel => {
  const groupField = resolveSingleGroupField(groupFields)
  if (groupField === undefined) return filterModel
  return {
    ...filterModel,
    items: filterModel.items.map((item) =>
      item.field === ROW_GROUPING_SINGLE_GROUPING_FIELD ? { ...item, field: groupField } : item
    ),
  }
}

// Transforms MUI's data grid model into our own model
// This function includes safe parsing so we have expected (operator, valueTypes) pairs for backend filtering
export function transformMuiFilterModelToOmnea({
  filterModel,
  filterNullValues = false,
  locale,
  groupFields,
}: TransformFilterModelOptions): GridFilterModel | undefined {
  if (filterModel == null) {
    return undefined
  }

  const groupField = resolveSingleGroupField(groupFields)

  const items = filterModel.items
  if (items.length === 0 && !filterModel.quickFilterValues) {
    return undefined
  }

  const processedItems = filterNullValues
    ? items.filter(
        (item) =>
          item.operator === 'isEmpty' || item.operator === 'isNotEmpty' || (item.value !== null && item.value !== undefined)
      )
    : items

  const transformedItems = processedItems.flatMap((item) => {
    const field = resolveFilterField(item.field, groupFields, groupField)
    if (field === undefined) return []

    const parsed = GridFilterItemSchema.safeParse({ ...item, field })

    if (!parsed.success) {
      console.error('Failed to parse filter item:', parsed.error)
      return []
    }

    return [parsed.data]
  })

  if (transformedItems.length === 0 && !filterModel.quickFilterValues) {
    return undefined
  }

  const enrichedItems = locale ? transformedItems.map((item) => enrichFilterItemWithLocale(item, locale)) : transformedItems

  const logicOperator = filterModel?.logicOperator
  return {
    items: enrichedItems,
    ...(logicOperator === GridLogicOperator.And
      ? { logicOperator: 'and' }
      : logicOperator === GridLogicOperator.Or
        ? { logicOperator: 'or' }
        : {}),
    quickFilterValues: filterModel?.quickFilterValues,
  }
}

const enrichFilterItemWithLocale = (item: GridFilterItem, locale: AvailableLocale): GridFilterItem => {
  if (item.field === 'address' && item.operator === 'contains') {
    return { ...item, context: { locale } }
  }
  return item
}

export function transformMuiSortModelToOmnea({ sortModel, groupFields }: TransformSortModelOptions) {
  if (!sortModel || sortModel.length === 0) {
    return undefined
  }

  const groupField = resolveSingleGroupField(groupFields)

  // Sorting by the group-column header sends MUI's grouping pseudo-field. Map it to the grouping field so
  // the server keeps the intended group ordering; strip it when there is no single grouping field to map
  // onto, since forwarding the pseudo-field would be an unknown column server-side.
  return sortModel
    .filter((item) => item.sort !== null)
    .flatMap((item) => {
      const field = item.field === ROW_GROUPING_SINGLE_GROUPING_FIELD ? groupField : item.field
      if (field === undefined) return []
      return [{ field, sort: item.sort as 'asc' | 'desc' }]
    })
}

export function transformOmneaFilterModelToMui(filterModel?: GridFilterModel): MuiGridFilterModel {
  if (!filterModel) {
    return { items: [] }
  }

  const { items, logicOperator, quickFilterValues } = filterModel

  return {
    items,
    ...(logicOperator ? { logicOperator: convertLogicOperatorToMui(logicOperator) } : {}),
    quickFilterValues,
  }
}

const convertLogicOperatorToMui = (logicOperator: 'and' | 'or'): GridLogicOperator => {
  if (logicOperator === 'and') {
    return GridLogicOperator.And
  }
  if (logicOperator === 'or') {
    return GridLogicOperator.Or
  }
  throw new AssertNeverError(logicOperator)
}
